import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, Form, Modal, Select, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useChartAccountsByType } from '@/hooks/useSalesInvoice';
import { useAuthStore } from '@/store/authStore';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import {
  directPurchaseInvoiceService,
  type VatConfigurationOption,
} from '@/services/directPurchaseInvoice.service';

const { Text } = Typography;

export type ConvertCertificationToSiPayload = {
  departmentId: string;
  expenseAccountId: string;
  vatConfigurationId?: string | null;
  inputVatAccountId?: string | null;
  applyVat: boolean;
};

type Props = {
  open: boolean;
  confirming?: boolean;
  title?: string;
  /** Prefill from Subcontractor Agreement / project when known. */
  defaultDepartmentId?: string | null;
  onCancel: () => void;
  onConfirm: (payload: ConvertCertificationToSiPayload) => void;
};

/**
 * Department + debit (expense) + VAT selection before converting a certified SPC
 * to Draft DirectExpense SI. Mirrors Finance DPI fields — subcontract-admin never posts GL.
 */
export function ConvertCertificationToSiModal({
  open,
  confirming,
  title = 'Convert to Draft Supplier Invoice',
  defaultDepartmentId,
  onCancel,
  onConfirm,
}: Props) {
  const companyId = useAuthStore((s) => s.user?.company_id);
  const [form] = Form.useForm<{
    departmentId: string;
    expenseAccountId: string;
    applyVat: boolean;
    vatConfigurationId?: string;
    inputVatAccountId?: string;
  }>();
  const applyVat = Form.useWatch('applyVat', form);
  const vatId = Form.useWatch('vatConfigurationId', form);
  const [vatHint, setVatHint] = useState<string | null>(null);

  const {
    data: vatConfigs = [],
    isError: vatConfigsError,
    error: vatConfigsLoadError,
    isLoading: vatLoading,
  } = useQuery({
    queryKey: ['dpi-vat-configs', companyId || ''],
    queryFn: () => directPurchaseInvoiceService.getVatConfigurations(),
    enabled: open && !!companyId,
  });

  const {
    data: expenseAccounts = [],
    isLoading: expenseLoading,
    isError: expenseError,
  } = useChartAccountsByType('Expense', false);
  const { data: assetAccounts = [] } = useChartAccountsByType('Asset', false);

  const expenseOptions = useMemo(
    () =>
      expenseAccounts.map((a) => ({
        value: a.id,
        label: `${a.account_code} - ${a.account_name}`,
        code: a.account_code,
      })),
    [expenseAccounts]
  );

  const vatOptions = useMemo(
    () =>
      (Array.isArray(vatConfigs) ? vatConfigs : []).map((v: VatConfigurationOption) => ({
        value: v.id,
        label: v.description
          ? `${v.description} (${Number(v.vat_rate)}%)`
          : `${v.vat_type} ${Number(v.vat_rate)}%`,
        rate: Number(v.vat_rate),
        suggestedGlAccountId: v.gl_account_id ?? v.gl_account?.id ?? null,
      })),
    [vatConfigs]
  );

  const inputVatGlOptions = useMemo(
    () =>
      assetAccounts.map((a) => ({
        value: a.id,
        label: `${a.account_code} - ${a.account_name}`,
      })),
    [assetAccounts]
  );

  const selectedVat = useMemo(
    () => vatOptions.find((v) => v.value === vatId),
    [vatOptions, vatId]
  );
  const needsInputVatGl = !!applyVat && (selectedVat?.rate ?? 0) > 0;

  useEffect(() => {
    if (!open) return;
    const preferred =
      vatOptions.find((v) => v.rate === 5 && /standard/i.test(String(v.label))) ||
      vatOptions.find((v) => v.rate > 0);
    const expenseDefault =
      expenseOptions.find((o) => o.code === '5002')?.value ||
      expenseOptions.find((o) => o.code === '5110')?.value ||
      expenseOptions[0]?.value;
    form.setFieldsValue({
      departmentId: defaultDepartmentId || undefined,
      expenseAccountId: expenseDefault,
      applyVat: !!preferred,
      vatConfigurationId: preferred?.value,
      inputVatAccountId:
        preferred?.suggestedGlAccountId &&
        inputVatGlOptions.some((o) => o.value === preferred.suggestedGlAccountId)
          ? preferred.suggestedGlAccountId
          : undefined,
    });
    setVatHint(
      preferred
        ? null
        : 'No taxable VAT codes found for this company. Uncheck Apply VAT or configure VAT under Finance.'
    );
    // Only reset when modal opens / option lists first load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, expenseOptions.length, vatOptions.length, inputVatGlOptions.length, defaultDepartmentId]);

  const onVatChange = (nextVatId?: string) => {
    const vc = vatOptions.find((v) => v.value === nextVatId);
    const patch: {
      vatConfigurationId?: string;
      inputVatAccountId?: string;
    } = { vatConfigurationId: nextVatId };
    if (!nextVatId || (vc?.rate ?? 0) <= 0) {
      patch.inputVatAccountId = undefined;
    } else if (
      vc?.suggestedGlAccountId &&
      inputVatGlOptions.some((o) => o.value === vc.suggestedGlAccountId)
    ) {
      patch.inputVatAccountId = vc.suggestedGlAccountId;
    }
    form.setFieldsValue(patch);
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      okText="Create Draft SI"
      confirmLoading={!!confirming}
      okButtonProps={{ disabled: expenseLoading || !expenseOptions.length }}
      destroyOnClose
      onOk={async () => {
        try {
          const values = await form.validateFields();
          if (values.applyVat && needsInputVatGl && !values.inputVatAccountId) {
            form.setFields([
              {
                name: 'inputVatAccountId',
                errors: ['Select Input VAT GL account when VAT rate is greater than 0'],
              },
            ]);
            return;
          }
          onConfirm({
            departmentId: values.departmentId,
            expenseAccountId: values.expenseAccountId,
            applyVat: !!values.applyVat,
            vatConfigurationId: values.applyVat ? values.vatConfigurationId || null : null,
            inputVatAccountId:
              values.applyVat && needsInputVatGl ? values.inputVatAccountId || null : null,
          });
        } catch {
          /* validation */
        }
      }}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Choose department, expense (debit) account, and VAT for the Draft Direct Purchase Invoice.
        Finance owns AP/GL posting — this step only creates the draft.
      </Text>
      {!expenseLoading && !expenseOptions.length ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="No leaf Expense accounts for this company"
          description={
            <span>
              Add them under{' '}
              <Link to="/fin/gl/chart-of-accounts" target="_blank">
                Finance → Chart of Accounts
              </Link>
              : Account type <strong>Expense</strong>, <strong>Is group = No</strong> (do not use
              header 5100). You can also use <strong>Seed Chart of Accounts</strong> there if the
              chart is empty. Then reopen Convert SI and pick e.g. 5002 Cost of Sales.
            </span>
          }
        />
      ) : null}
      {expenseError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Could not load expense accounts"
          description="Check you are logged into the same company as the Subcontractor Agreement."
        />
      ) : null}
      {vatConfigsError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Could not load VAT codes"
          description={
            (vatConfigsLoadError as { response?: { data?: { message?: string } } })?.response?.data
              ?.message || (vatConfigsLoadError as Error)?.message
          }
        />
      ) : null}
      {vatHint ? (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={vatHint} />
      ) : null}
      <Form form={form} layout="vertical" initialValues={{ applyVat: true }}>
        <Form.Item
          name="departmentId"
          label="Department"
          rules={[{ required: true, message: 'Department is required' }]}
        >
          <DepartmentSelect placeholder="Select department" />
        </Form.Item>
        <Form.Item
          name="expenseAccountId"
          label="Debit (expense) account"
          rules={[{ required: true, message: 'Select expense account' }]}
          extra={
            <span>
              From{' '}
              <Link to="/fin/gl/chart-of-accounts" target="_blank">
                Chart of Accounts
              </Link>{' '}
              (leaf Expense only).
            </span>
          }
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={expenseLoading ? 'Loading accounts…' : 'Expense GL account'}
            options={expenseOptions}
            loading={expenseLoading}
          />
        </Form.Item>
        <Form.Item name="applyVat" valuePropName="checked">
          <Checkbox
            onChange={(e) => {
              if (!e.target.checked) {
                form.setFieldsValue({
                  vatConfigurationId: undefined,
                  inputVatAccountId: undefined,
                });
              } else {
                const preferred =
                  vatOptions.find((v) => v.rate === 5) || vatOptions.find((v) => v.rate > 0);
                if (preferred) onVatChange(preferred.value);
              }
            }}
          >
            Apply VAT
          </Checkbox>
        </Form.Item>
        {applyVat ? (
          <>
            <Form.Item
              name="vatConfigurationId"
              label="VAT code"
              rules={[{ required: true, message: 'Select VAT code' }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={vatLoading ? 'Loading VAT…' : 'VAT configuration'}
                options={vatOptions}
                onChange={(v) => onVatChange(v)}
                loading={vatLoading}
              />
            </Form.Item>
            <Form.Item
              name="inputVatAccountId"
              label="Input VAT account"
              rules={
                needsInputVatGl
                  ? [{ required: true, message: 'Select Input VAT GL account' }]
                  : undefined
              }
              extra={
                needsInputVatGl
                  ? 'Asset GL for recoverable input VAT (same as Direct Purchase Invoice).'
                  : 'Not required when VAT rate is 0%.'
              }
            >
              <Select
                allowClear={!needsInputVatGl}
                showSearch
                optionFilterProp="label"
                placeholder={needsInputVatGl ? 'Select input VAT account' : '—'}
                disabled={!needsInputVatGl}
                options={inputVatGlOptions}
              />
            </Form.Item>
          </>
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
            message="Draft SI will be created exclusive of VAT. Finance can add VAT on the invoice later."
          />
        )}
      </Form>
    </Modal>
  );
}
